/**
 * "Let's get some details on these accounts" — account capture, shared by guided planning and the D2C
 * self-funding sequence.
 *
 * The host passes the selected funding-source types, the funding sources to show (live, or a guided
 * snapshot), loading/saving/read-only state and the last save error, the flow's account requirement,
 * how partner shares are recorded, and Continue/Back. Accounts save themselves through the module's
 * funding-source persistence; Continue ("Calculate My Coverage") is enabled only once every started
 * account is complete (and, when required, at least one self-funding account is).
 */
import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle, Button, Skeleton, cn } from '@design-system';

import { AccountAccordionList } from '../components/account-accordion-list';
import type { AssetTypeId } from '../config/funding-source-types';
import type { LegacyPartnerSelections } from '../hooks/use-partner-locked-funding-sources';
import type { PersistedSelection } from '../hooks/use-partner-allocation';
import type { FundingScreenFrame } from '../types';

export interface FundingSourceAccountsProps {
  selectedTypes: readonly AssetTypeId[];
  /** Funding sources to show: the client's live sources, or a completed guided session's snapshot. */
  fundingSources: MultipleFundingSources | undefined;
  isLoading: boolean;
  isSaving: boolean;
  saveError: { message: string; retry: () => void } | null;
  readOnly: boolean;
  requireSelfFundingAccount: boolean;
  /**
   * Set while the host's plan is finalized: each account save waits for it, and is dropped if it
   * resolves false. Existing policies stay view-only meanwhile.
   */
  beforeAccountSave?: () => Promise<boolean>;
  /** A completed guided session's allocations, shown as "(−amount)" on each account. */
  completedAllocations?: Record<string, number>;
  savedAllocationSelections?: Record<string, PersistedSelection>;
  persistAllocationSelections?: (
    selections: Record<string, PersistedSelection>,
  ) => Promise<unknown>;
  /** Legacy partner shares from the partner's guided session (guided planning only). */
  legacyPartnerSelections?: LegacyPartnerSelections;
  onContinue: () => void;
  onBack?: () => void;
  /** Fill the host's width (the consumer flow) instead of a centred column (guided planning). */
  fullWidth?: boolean;
  frame: FundingScreenFrame;
}

export function FundingSourceAccounts({
  selectedTypes,
  fundingSources,
  isLoading,
  isSaving,
  saveError,
  readOnly,
  requireSelfFundingAccount,
  beforeAccountSave,
  completedAllocations,
  savedAllocationSelections,
  persistAllocationSelections,
  legacyPartnerSelections,
  onContinue,
  onBack,
  fullWidth = false,
  frame,
}: FundingSourceAccountsProps) {
  const [accountsComplete, setAccountsComplete] = useState(false);
  const widthClass = fullWidth ? 'w-full' : 'w-full max-w-2xl';

  const content = (
    <div className="flex flex-col items-center gap-4">
      {saveError && (
        <Alert
          variant="destructive"
          className={widthClass}
        >
          <AlertTitle>We couldn’t save your last change</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-2">
            {saveError.message}
            <Button
              variant="outline"
              size="sm"
              onClick={saveError.retry}
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {isLoading ? (
        <div className={cn(widthClass, 'space-y-4')}>
          <Skeleton className="h-16 rounded-lg" />
          <Skeleton className="h-16 rounded-lg" />
        </div>
      ) : (
        <AccountAccordionList
          className={widthClass}
          selectedTypes={selectedTypes}
          fundingSources={fundingSources}
          readOnly={readOnly}
          requireSelfFundingAccount={requireSelfFundingAccount}
          beforeAccountSave={beforeAccountSave}
          completedAllocations={completedAllocations}
          savedAllocationSelections={savedAllocationSelections}
          persistAllocationSelections={persistAllocationSelections}
          legacy={legacyPartnerSelections}
          onAllCompleteChange={setAccountsComplete}
        />
      )}
    </div>
  );

  return frame({
    title: "Let's get some details on these accounts",
    subtitle:
      'Click on each account to add the financial details. This helps us accurately project your long-term care coverage capacity.',
    content,
    primaryAction: {
      label: 'Calculate My Coverage',
      onSelect: onContinue,
      disabled: !accountsComplete || isLoading || isSaving,
      busy: isSaving,
    },
    backAction: onBack ? { label: 'Back', onSelect: onBack } : undefined,
  });
}
